<template>
  <div v-if="event || org">
    <v-container v-if="insights">
      <h4>Period <span v-if="snapshot">{{ snapshot.period_type }} {{ snapshot.period }}</span></h4>
      <p v-if="snapshot">
        Based on period running from 
        {{snapshot.from | localDate('D', true) }}
        till
        {{snapshot.till | localDate('D', true) }}
        <v-dialog v-if="insights.snapshots.length > 1" v-model="showPeriodDialog" max-width="400px">
          <template v-slot:activator="{ on }">
            <v-btn text small color="primary" class="" v-on="on">select</v-btn>
          </template>
          <v-card>
            <v-card-title>Select Period</v-card-title>
            <v-card-text>
              Select the period to view.
            </v-card-text>
            <v-divider></v-divider>
            <v-list>
              <template v-for="(item, idx) in insights.snapshots.filter(x => x.scores != null)">
                <v-list-item :key="idx">
                  <v-list-item-content>
                  <v-list-item-title>{{item.from | localDate('D', true) }} - {{item.till | localDate('D', true) }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn text color="primary" @click="snapshot=item;showPeriodDialog = false;"> {{$t('shared.select')}}</v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider  :key="`sep-${idx}`"/>
              </template>
            </v-list>
            <v-card-actions>
              <v-btn color="secondary" text @click="showPeriodDialog = false">{{$t('shared.cancel')}}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>   
        .
      </p>
      <p v-if="snapshot" class="text-muted">
        Metrics are calculated on {{ snapshot.calc_ts | localDate('ddd L LTS') }}
      </p>
      <v-alert v-if="snapshot && snapshot.partial" type="info" text>
        Reporting is based on a partial week (because either the start or end of the event was within this period). 
        This might impact the results of this period compared to other periods.
      </v-alert>
      <p v-if="!snapshot">
        Please wait until one week after the event has started for the first metrics to start showing.
      </p>
      <div v-if="snapshot && snapshot.scores">
        <h4>Key Metrics</h4>
        <v-btn v-if="false" color="primary" text @click="downloadReport('insights-improvers', `${event.name}-insights-improvers`)">Download</v-btn>
      </div>
      <v-row v-if="snapshot && snapshot.scores">
        <v-col cols="12" sm="4" md="4">
          <v-card class="white--text" color="primary darken-4">
            <v-card-title class="white--text ">
              Engagement Score
              <v-spacer/>
              <v-btn icon class="ma-0" @click="engagementScoreExpanded = !engagementScoreExpanded">
                <v-icon color="white">{{ engagementScoreExpanded ? 'fa fal fa-chevron-circle-up' : 'fa fal fa-chevron-circle-down' }}</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="white--text pb-0" style="opacity:.7;">
              Overall engagement score with this event.
            </v-card-text>
            <div v-if="snapshot && snapshot.scores.engagement_score">
              <v-card-title class="subtitle">
                <span>{{ snapshot.scores.engagement_score }}%</span>
                <span v-if="engagementScoreExpanded" class="ms-1">(out of {{ snapshot.scores.engagement_count }})</span>
                <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" :text="`Percentage of participants engaged with this event. Out of ${snapshot.scores.engagement_count} participants who have joined this event.`"/>
              </v-card-title>
              <!-- <v-card-title v-else class="subtitle">n/a</v-card-title> -->
            </div>
            <div v-if="false && engagementScoreExpanded">
              <p class="subtitle">Participation: {{ snapshot.scores.participation_score }}%</p>
              <p class="subtitle">Social: {{ snapshot.scores.social_score }}%</p>
            </div>
            <v-sparkline :labels="engagementScoresSparklineLabels" :value="engagementScoresSparklineValues" type="trend" smooth />
          </v-card>
        </v-col>
        <v-col cols="12" sm="4" md="4">
          <v-card class="white--text" color="primary darken-3">
            <v-card-title class="white--text ">
              Activity Score
              <v-spacer/>
              <v-btn icon class="ma-0" @click="activityScoreExpanded = !activityScoreExpanded">
                <v-icon color="white">{{ activityScoreExpanded ? 'fa fal fa-chevron-circle-up' : 'fa fal fa-chevron-circle-down' }}</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="white--text pb-0" style="opacity:.7;">
              Percentage increase in activity by all participants.
            </v-card-text>
            <div v-if="snapshot && snapshot.pts_vs_baseline">
              <v-card-title class="subtitle">
                <span>{{ snapshot.pts_vs_baseline > 1 ? '+' : '' }}</span>
                <span>{{ (snapshot.pts_vs_baseline - 1) * 100 | int }}%</span>
                <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" text="Average points per period by all participants, compared with baseline."/>
              </v-card-title>
            </div>
            <v-card-title v-else class="subtitle">Not available (yet)</v-card-title>
            <v-sparkline :labels="activityScoresSparklineLabels" :value="activityScoresSparklineValues" type="trend" smooth />
          </v-card>
        </v-col>
        <v-col cols="12" sm="4" md="4">
          <v-card class="white--text" color="primary darken-2">
            <v-card-title class="white--text ">
              Improvers Score
              <simple-tooltip v-if="false" color="white" icon-class="ms-2" bottom text="Percentage of participants moving more or reaching daily healthy target. Compared with performance in week before event started, or scaled first week if no other data is available."/>
              <v-spacer />
              <v-btn icon color="white" @click="moveScoreExpanded = !moveScoreExpanded">
                <v-icon>{{ moveScoreExpanded ? 'fa fal fa-chevron-circle-up' : 'fa fal fa-chevron-circle-down' }}</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text class="white--text pb-0" style="opacity:.7;">
              Percentage participants who move more.
            </v-card-text>
            <v-card-title v-if="snapshot && snapshot.scores.move_score" class="subtitle">
              <span>{{ snapshot.scores.move_score }}% </span>
              <span v-if="moveScoreExpanded" class="ms-1">(out of {{ snapshot.scores.move_count }})</span>
              <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" text="Percentage of participants moving more (or reaching daily healthy target). Compared with performance in week before event started, or scaled first week if no other data is available."/>
            </v-card-title>
            <v-card-title v-else class="subtitle">Not available (yet)</v-card-title>
            <v-card-text v-if="moveScoreExpanded" class="white--text">
              
            </v-card-text>
            <div v-if="moveScoreExpanded" class="">
              <v-card-title class="pb-0">
                Significant Improvers score
              </v-card-title>
              <v-card-title class="subtitle">
                <span>{{ snapshot.scores.tier_score }}% </span>
                <span v-if="moveScoreExpanded" class="ms-1">(out of {{ snapshot.scores.tier_count }})</span>
                <simple-tooltip color="white" bottom tooltip-color="info" icon-class="ms-2" text="Percentage of participants improved a league (or already at max league). Compared with performance in week before event started."/>
              </v-card-title>
            </div>
            <v-sparkline :labels="moveScoresSparklineLabels" :value="moveScoresSparklineValues" type="trend" smooth />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="snapshot && snapshot.podiums" class="mt-0">
      <h4>Podiums</h4>
      <p>Most popular activity types for participants of this event.</p>
      <p>
        <v-btn  v-if="false" color="primary" text @click="downloadReport('insights-podiums', `${event.name}-insights-podiums`)">Download</v-btn>
      </p>
      <v-row>
        <v-col v-for="(item, idx) in snapshot.podiums" :key="idx" cols="12" sm="4" md="4">
          <v-card class="white--text" :color="`accent darken-${idx}`">
            <v-card-title class="white--text force-one-line" :title="$helpers.getActivityTypeLabel(item.activity_type, event)">
              <!-- <v-icon color="white" small class="me-2">{{ $helpers.getActivityIcon(item.activity_type) }}</v-icon> -->
              {{ $helpers.getActivityTypeLabel(item.activity_type, event) }}
            </v-card-title>
            <v-card-text class="white--text">{{ item.stats.user_count}} participants</v-card-text>
            <!-- <v-card-text class="white--text">Supported: {{ item.stats.supported}}?</v-card-text> -->
            <v-divider style="border-color: #ffffff33;" />
            <v-card-text>
            <div v-for="( profile, pos) in item.profiles" :key="pos" class="white--text pb-0 pt-2">#{{ pos+1 }}. {{ profile.name }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-if="snapshot" class="mt-0">
      <h4>Leagues</h4>
      <p>Participants per league. Leagues are calculated based on the average points per day.</p>
      <p>
        <v-btn v-if="false" color="primary" text @click="downloadReport('insights-leagues', `${event.name}-insights-leagues`)">Download</v-btn>
      </p>
      <v-row  v-if="snapshot.assessment && snapshot.assessment.tiers">
        <v-col v-for="(item, idx) in snapshot.assessment.tiers" :key="idx" cols="12" sm="4" md="3">
          <v-card class=""  :style="{'border-color': `var(--v-league-${item.tier}-base)`, 'border-style': 'solid', 'border-width': '5px'}">
            <v-card-title class="force-one-line pb-0" >
              {{ item.tier | league }} 
            </v-card-title>
            <v-card-text class="pb-0" style="opacity:.7;">
              <span v-if="item.tier == 1">0 - 3k pts/day</span>
              <span v-if="item.tier == 2">3k - 6k pts/day</span>
              <span v-if="item.tier == 3">6k - 10k pts/day</span>
              <span v-if="item.tier == 4">10k+ pts/day</span>
            </v-card-text>
            <v-card-text class="">{{ item.count}} participants</v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <p v-else>
        This data is not available yet. Please check back in a few days.
      </p>
    </v-container>

  </div>
</template>

<script>
import { DateTime } from 'luxon'
import siteData from '@/data/site.json'
import EventUtil from "@/util/eventUtil";
import OrgUtil from "@/util/orgUtil";
import eventManagerService from "@/services/eventManagerService";
import orgManagerService from "@/services/orgManagerService";
import SimpleTooltip from '@/components/SimpleTooltip.vue';

export default {
  name: "InsightsSummary",
  components: {
    SimpleTooltip,
  },
  props: {
    //event: Object,
    //org: Object,
  },
  data() {
    return {
      siteData: siteData,
      insights: null,
      snapshot: null,
      event: null,
      org: null,
      showPeriodDialog: false,
    };
  },
  async mounted() {
  },
  methods: {

    async loadForEvent(event) {
      this.event = event;
      this.insights = (await eventManagerService.getInsights(this.event.id, /* date: */ null, /* currentOnly: */false)).data;
      this.snapshot = this.insights && this.insights.latest;
      console.log('/// current insights', this.snapshot);
    },

    async loadForOrg(org) {
      this.org = org;
      this.insights = (await orgManagerService.getInsights(this.org.id, /* date: */ null, /* currentOnly: */false)).data;
      this.snapshot = this.insights && this.insights.latest;
      console.log('/// current insights', this.snapshot);
    },

  },

  computed: {
    eventMode() { return this.event != null; },

    eventUtil() {
      return new EventUtil(this, this.event);
    },

    orgUtil() {
      return new OrgUtil(this, this.org);
    },

    engagementScoresSparklineLabels() {
      return this.insights == null || this.insights.snapshots == null
             ? []
             : [ 'Start', ...this.insights.snapshots.filter(x => x.scores && x.scores.engagement_score).map(x => this.$options.filters.localDate(x.from, 'XS'))];
    },
    engagementScoresSparklineValues() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 0, ...this.insights.snapshots.filter(x => x.scores && x.scores.engagement_score).map(x => x.scores.engagement_score)];
    },
    activityScoresSparklineLabels() {
      return this.insights == null || this.insights.snapshots == null
             ? []
             : [ 'Start', ...this.insights.snapshots.filter(x => x.pts_vs_baseline).map(x => this.$options.filters.localDate(x.from, 'XS'))];
    },
    activityScoresSparklineValues() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 0, ...this.insights.snapshots.filter(x => x.pts_vs_baseline).map(x => x.pts_vs_baseline)];
    },
    moveScoresSparklineLabels() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 'Start', ...this.insights.snapshots.filter(x => x.scores && x.scores.move_score).map(x => this.$options.filters.localDate(x.from, 'XS'))];
    },
    moveScoresSparklineValues() {
      return this.insights == null  || this.insights.snapshots == null
             ? []
             : [ 0, ...this.insights.snapshots.filter(x => x.scores && x.scores.move_score).map(x => x.scores.move_score)];
    },

  },

};
</script>
<style lang="scss">
.v-card__subtitle {
  padding-bottom: 0;
}

.row--separated {
  .col + .col {
    border-left: solid 1px #ccc;
  }
}
.container--min-height {
  padding-top: 0;
  padding-bottom: 0;
  .row > div { 
    padding-top: 0;
    padding-bottom: 0;
   }
}

.chart-floating-label {
  position: absolute;
  right: 0;
  bottom: 0;
}
.inline-bar {
  border: solid 1px #aaa;
  background-color: #ccc;
  margin: 8px 4px;
}
.inline-bar.active {
  border-color: black;
  background-color: #333;
}
.bar-chart-label {
  padding-top: 2px;
}
.not-bold { font-weight: normal;}
</style>

